import { useEffect } from 'react';

// Helper to push events to dataLayer
const trackEvent = (eventName: string, eventData: Record<string, any> = {}) => {
  // Make sure dataLayer exists
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...eventData
    });
    console.log(`Event tracked: ${eventName}`, eventData);
  }
};

// Helper to get section name from element
const getSectionName = (element: HTMLElement): string => {
  // Check for section in data attribute
  const dataSection = element.getAttribute('data-section');
  if (dataSection) return dataSection;

  // Try to infer section from parent elements with IDs or classes
  let currentElement: HTMLElement | null = element;
  let depth = 0;
  const maxDepth = 5; // Limit how far up we search
  
  while (currentElement && depth < maxDepth) {
    // Check for section identifiers in class names
    const classNames = currentElement.className?.split(' ') || [];
    
    // Common section identifiers
    const sectionKeywords = ['hero', 'cta', 'footer', 'header', 'nav', 'directions', 'pricing'];
    for (const className of classNames) {
      for (const keyword of sectionKeywords) {
        if (className.toLowerCase().includes(keyword)) {
          return keyword;
        }
      }
    }
    
    // Check for section in ID
    if (currentElement.id) {
      const id = currentElement.id.toLowerCase();
      for (const keyword of sectionKeywords) {
        if (id.includes(keyword)) {
          return keyword;
        }
      }
    }
    
    currentElement = currentElement.parentElement;
    depth++;
  }
  
  // Default section name if we couldn't determine it
  return 'unknown';
};

// Helper to infer event name from element
const inferEventName = (element: HTMLElement, isZenoti: boolean = false): string => {
  // First check if there's an explicit data-event attribute
  const dataEvent = element.getAttribute('data-event');
  if (dataEvent) return dataEvent;
  
  // Get section name
  const section = getSectionName(element);
  
  // Get action based on element type and content
  let action = '';
  
  // Check if it's a link to a specific platform
  const href = element instanceof HTMLAnchorElement ? element.href : '';
  
  if (isZenoti) {
    action = 'zenoti';
    // Try to determine specific zenoti action
    if (element.textContent?.toLowerCase().includes('schedule')) {
      action += '_schedule';
    } else if (element.textContent?.toLowerCase().includes('consult')) {
      action += '_consultation';
    } else if (element.textContent?.toLowerCase().includes('get started')) {
      action += '_get_started';
    } else {
      action += '_link';
    }
  } else if (href?.includes('maps.google') || href?.includes('maps.app.goo.gl')) {
    action = 'map_google';
  } else if (href?.includes('maps.apple')) {
    action = 'map_apple';
  } else if (href?.includes('waze')) {
    action = 'map_waze';
  } else if (href?.startsWith('tel:')) {
    action = 'phone';
  } else if (element.tagName === 'BUTTON' || element.tagName === 'A') {
    // Get the button/link text
    const text = element.textContent?.trim().toLowerCase() || '';
    
    if (text.includes('direction') || text.includes('walk in')) {
      action = text.includes('direction') ? 'get_directions' : 'walk_in';
    } else if (text.includes('schedule')) {
      action = 'schedule';
    } else if (text.includes('close') || element.classList.contains('close') || element.querySelector('.close')) {
      action = 'close';
    } else {
      // Use the first 2-3 words of the button text as the action
      action = text.split(' ').slice(0, 3).join('_').replace(/[^a-z0-9_]/g, '');
    }
  }
  
  // Construct the event name
  return `${section}_${action}_click`;
};

// Main event tracker component
const EventTracker = () => {
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      // Find the clickable element (button, a, or any element with role="button")
      let target = event.target as HTMLElement | null;
      let clickableElement: HTMLElement | null = null;
      
      // Traverse up to find the clickable element
      while (target && !clickableElement) {
        if (
          target.tagName === 'BUTTON' || 
          target.tagName === 'A' || 
          target.getAttribute('role') === 'button' ||
          target.classList.contains('clickable')
        ) {
          clickableElement = target;
        }
        target = target.parentElement;
      }
      
      if (!clickableElement) return;
      
      // Check if it's a Zenoti link
      const isZenotiLink = 
        clickableElement instanceof HTMLAnchorElement && 
        clickableElement.href?.includes('zenoti.com');
      
      // Infer the event name
      const eventName = inferEventName(clickableElement, isZenotiLink);
      
      // Get additional data to track
      const eventData: Record<string, any> = {
        element_text: clickableElement.textContent?.trim() || '',
        element_type: clickableElement.tagName.toLowerCase(),
        is_external: clickableElement instanceof HTMLAnchorElement && 
                    clickableElement.hostname !== window.location.hostname,
      };
      
      // If it's a link, add the href
      if (clickableElement instanceof HTMLAnchorElement) {
        eventData.href = clickableElement.href;
      }
      
      // Track the event
      trackEvent(eventName, eventData);
    };
    
    // Add the event listener to the document
    document.addEventListener('click', handleClick, true);
    
    // Clean up
    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, []);
  
  return null;
};

// Add to window for direct access
if (typeof window !== 'undefined') {
  window.trackEvent = trackEvent;
}

// TypeScript declaration
declare global {
  interface Window {
    dataLayer: any[];
    trackEvent: (eventName: string, eventData?: Record<string, any>) => void;
  }
}

export default EventTracker; 